"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calendar, FileCheck2, Building2, Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function History() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.38,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="lịch-sử-thành-lập" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary bg-accent rounded-sm border border-border">
            <Building2 className="size-3.5" />
            <span>Mốc son phát triển</span>
          </div>

          <div className="mt-4 flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b border-border relative">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-secondary">
                Lịch sử thành lập
              </h2>
              <p className="mt-2 text-base text-muted-foreground max-w-2xl">
                Quá trình chuẩn bị bài bản và dấu mốc đại hội thành lập, khẳng định cơ sở pháp lý vững chắc của Hội Địa lý môi trường Việt Nam.
              </p>
            </div>
            {/* Archival indicator rule */}
            <div className="absolute -bottom-[2px] left-0 w-16 h-[3px] bg-secondary" />
          </div>
        </div>

        {/* 2-Column Chronicle Layout */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: Historical Records */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <motion.div variants={itemVariants}>
              <Card className="border border-border bg-card shadow-xs rounded-sm overflow-hidden">
                <div className="h-1 bg-primary w-full" />
                <CardContent className="p-6 sm:p-8 space-y-6">
                  {/* Exact Date Milestone */}
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground font-semibold text-sm rounded-sm">
                      <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                      06/5/2021
                    </span>
                    <span className="text-sm font-medium text-muted-foreground">
                      Nhà khách Chính phủ, số 2 Lê Thạch, Hà Nội
                    </span>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
                      Đại hội thành lập Hội Địa lý môi trường Việt Nam
                    </h3>
                    <p className="text-sm sm:text-base leading-relaxed text-muted-foreground">
                      Ngày 06/5/2021, tại Nhà khách Chính phủ (số 2 Lê Thạch, Hà Nội), đã diễn ra Đại hội thành lập Hội Địa lý môi trường Việt Nam (VAEG), nhiệm kỳ 2021 - 2026. Đại hội chính thức khai sinh một tổ chức khoa học xã hội hoạt động trong lĩnh vực địa lý môi trường — nghiên cứu các quy luật tự nhiên diễn ra trên bề mặt Trái Đất và mối tương tác giữa con người với môi trường thiên nhiên, kết hợp kiến thức khoa học hiện đại với các giá trị địa lý truyền thống phương Đông. Sự kiện quy tụ đội ngũ nhà khoa học, giảng viên, chuyên gia đầu ngành trong lĩnh vực địa lý, tài nguyên thiên nhiên và bảo vệ môi trường trên phạm vi cả nước, cùng đại diện các cơ quan quản lý Nhà nước.
                    </p>
                  </div>

                  {/* Legal Decisions Framework */}
                  <div className="space-y-3 pt-2">
                    <h4 className="text-sm font-semibold text-foreground tracking-wide uppercase">
                      Căn cứ pháp lý của Bộ Nội vụ
                    </h4>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      <div className="p-4 rounded-sm bg-muted/60 border border-border">
                        <div className="flex items-start gap-2.5">
                          <FileCheck2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs font-semibold uppercase text-primary tracking-wide">
                              Quyết định cho phép thành lập
                            </p>
                            <p className="text-sm font-medium text-foreground mt-1">
                              Số 483/QĐ-BNV
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Ban hành ngày 02/4/2021
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 rounded-sm bg-muted/60 border border-border">
                        <div className="flex items-start gap-2.5">
                          <FileCheck2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs font-semibold uppercase text-primary tracking-wide">
                              Quyết định phê duyệt Điều lệ
                            </p>
                            <p className="text-sm font-medium text-foreground mt-1">
                              Số 1157/QĐ-BNV
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Ban hành ngày 03/11/2021
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Summary of Roles */}
                  <div className="pt-2 border-t border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Users className="size-4 text-primary" />
                      <span>Tổ chức quy tụ các nhà khoa học và viện nghiên cứu toàn quốc</span>
                    </div>

                    <Button variant="outline" size="sm" asChild className="rounded-sm self-start sm:self-auto border-border text-foreground hover:bg-accent hover:text-accent-foreground">
                      <Link to="/OrganizationStructurePage">
                        Xem cơ cấu tổ chức
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Right Column: Documentary Photography & Video */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <motion.div variants={itemVariants} className="border border-border bg-card rounded-sm overflow-hidden p-2 shadow-xs">
              <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xs bg-muted">
                <img src="https://hoidialymoitruong.vn/wp-content/uploads/2026/09/0L0A9393-scaled.jpeg" alt="Hình ảnh phiên khai mạc Đại hội thành lập Hội Địa lý môi trường Việt Nam" className="h-full w-full object-cover" loading="lazy" />
              </div>

              {/* Archival Caption */}
              <div className="p-3.5 space-y-1">
                <p className="text-xs font-semibold text-primary uppercase tracking-wider">
                  Tư liệu lịch sử
                </p>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Hình ảnh phiên khai mạc Đại hội thành lập Hội Địa lý môi trường Việt Nam, tổ chức trang trọng tại Nhà khách Chính phủ, Hà Nội ngày 06/5/2021, với sự tham gia của các đại biểu, nhà khoa học và đại diện cơ quan quản lý Nhà nước.
                </p>
              </div>
            </motion.div>

            {/* Legal Citation Footnote */}
            <motion.div variants={itemVariants} className="p-4 rounded-sm bg-muted/40 border border-border">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Hội hoạt động trên phạm vi cả nước theo quy định pháp luật của nước Cộng hòa Xã hội Chủ nghĩa Việt Nam và chịu sự quản lý nhà nước của Bộ Nội vụ cùng các Bộ, ngành liên quan.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}